import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import countryToCurrency from "country-to-currency";
import type { Currency, Spot } from "../types";
import {
  FilterState,
  initialFilters,
  SourceKey,
  spotMatchesFilters,
} from "./filters";
import { fxFallback, FxRates, loadFxRates } from "./fxRates";

const SUPPORTED_CURRENCIES: Currency[] = ["ISK", "EUR", "USD", "CHF", "GBP"];

/** Walks the browser's language list, extracts the ISO-3166 region from
 *  each entry via Intl.Locale, looks up the country's currency through
 *  the `country-to-currency` package, and returns the first hit that
 *  matches one of our supported currencies. Falls back to ISK when no
 *  region can be detected or the country's currency isn't supported —
 *  Iceland is the topic, so ISK is the sensible default. */
function detectBrowserCurrency(): Currency {
  if (typeof navigator === "undefined") return "ISK";
  const locales = navigator.languages?.length
    ? [...navigator.languages]
    : navigator.language
      ? [navigator.language]
      : [];
  for (const loc of locales) {
    let region: string | undefined;
    try {
      region = new Intl.Locale(loc).region;
    } catch {
      const [, regionPart] = String(loc).split(/[-_]/);
      region = regionPart?.toUpperCase();
    }
    if (!region) continue;
    const code = (countryToCurrency as Record<string, string>)[region];
    if (code && SUPPORTED_CURRENCIES.includes(code as Currency)) {
      return code as Currency;
    }
  }
  return "ISK";
}

function readStoredCurrency(): Currency {
  try {
    const stored = localStorage.getItem("fishing-currency");
    if (stored && SUPPORTED_CURRENCIES.includes(stored as Currency)) {
      return stored as Currency;
    }
  } catch {
    /* ignore */
  }
  return detectBrowserCurrency();
}

type FilterAction =
  | { type: "set"; patch: Partial<Omit<FilterState, "enabledSources">> }
  | { type: "toggleSource"; key: SourceKey }
  | { type: "setSources"; keys: SourceKey[] }
  | { type: "reset" };

function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case "set":
      return { ...state, ...action.patch };
    case "toggleSource": {
      const next = new Set(state.enabledSources);
      if (next.has(action.key)) next.delete(action.key);
      else next.add(action.key);
      return { ...state, enabledSources: next };
    }
    case "setSources":
      return { ...state, enabledSources: new Set(action.keys) };
    case "reset":
      return initialFilters();
    default:
      return state;
  }
}

interface AppStateCtx {
  spots: Spot[];
  generatedAt: string | null;
  loading: boolean;
  loadError: string | null;
  filters: FilterState;
  dispatchFilter: (a: FilterAction) => void;
  visibleSpots: Spot[];
  selectedId: string | null;
  selectSpot: (id: string | null) => void;
  currency: Currency;
  setCurrency: (c: Currency) => void;
  rates: FxRates;
}

const Ctx = createContext<AppStateCtx | null>(null);

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [spots, setSpots] = useState<Spot[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, dispatchFilter] = useReducer(filterReducer, undefined, initialFilters);
  const [currency, setCurrencyState] = useState<Currency>(() => readStoredCurrency());
  const [rates, setRates] = useState<FxRates>(fxFallback);

  // selectedId is derived from the URL so the browser back button is the
  // single source of truth for "is a spot panel open". We never store it
  // separately — otherwise a state-vs-URL sync loop causes flicker.
  const location = useLocation();
  const navigate = useNavigate();
  const m = location.pathname.match(/\/spot\/([^/]+)/);
  const selectedId = m ? decodeURIComponent(m[1]) : null;

  const selectSpot = useCallback(
    (id: string | null) => {
      if (id === null) {
        if (selectedId) navigate(-1);
        return;
      }
      if (id === selectedId) return; // no-op: already showing
      // If a spot is already open, REPLACE the entry so the back stack
      // only ever contains one spot in a row.
      navigate(`/spot/${encodeURIComponent(id)}`, { replace: !!selectedId });
    },
    [navigate, selectedId],
  );

  const setCurrency = useCallback((c: Currency) => {
    setCurrencyState(c);
    try {
      localStorage.setItem("fishing-currency", c);
    } catch {
      /* ignore */
    }
  }, []);

  // Bootstrap: parallel-fetch FX rates + spot data.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      loadFxRates(),
      fetch(`${import.meta.env.BASE_URL}spots.json`, { cache: "no-cache" }).then((r) => {
        if (!r.ok) throw new Error(`spots http ${r.status}`);
        return r.json() as Promise<{ generatedAt: string; spots: Spot[] }>;
      }),
    ])
      .then(([fx, data]) => {
        if (!alive) return;
        setRates(fx);
        setSpots(data.spots);
        setGeneratedAt(data.generatedAt);
        setLoading(false);
      })
      .catch((err) => {
        console.error("bootstrap failed", err);
        if (!alive) return;
        setLoadError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const visibleSpots = useMemo(
    () => spots.filter((s) => spotMatchesFilters(s, filters)),
    [spots, filters],
  );

  const value = useMemo<AppStateCtx>(
    () => ({
      spots,
      generatedAt,
      loading,
      loadError,
      filters,
      dispatchFilter,
      visibleSpots,
      selectedId,
      selectSpot,
      currency,
      setCurrency,
      rates,
    }),
    [
      spots,
      generatedAt,
      loading,
      loadError,
      filters,
      visibleSpots,
      selectedId,
      selectSpot,
      currency,
      setCurrency,
      rates,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppState(): AppStateCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAppState must be inside AppStateProvider");
  return v;
}
